import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { auth, firestore } from "firebaseConfig";
import { onAuthStateChanged, updateProfile, updatePassword, deleteUser, signOut } from "firebase/auth";
import { collection, getDocs, deleteDoc, doc, updateDoc, onSnapshot } from "firebase/firestore";
import { createCheckoutSession, cancelAtPeriodEnd } from "services";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";

function MinhaConta() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // modais
  const [openEdit, setOpenEdit] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);

  // flags
  const [cancelLoading, setCancelLoading] = useState(false);

  // dados básicos
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // assinatura
  const [perfil, setPerfil] = useState(null);
  const [planLabel, setPlanLabel] = useState("Sem plano");
  const [statusLabel, setStatusLabel] = useState("sem assinatura");

  const navigate = useNavigate();

  // Observa auth e o doc do usuário em tempo real
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) return;

      setUserName(user.displayName || user.email.split("@")[0]);
      setUserEmail(user.email);

      const ref = doc(firestore, "usuarios", user.uid);
      const unsubDoc = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const d = snap.data();

        // log leve para depurar visibilidade de botões
        // console.log("perfil:", d);

        setPerfil({ id: snap.id, ...d });

        // Plano (aceita seus rótulos antigos também)
        let plan = d?.plan === "mensal" ? "Mensal" : d?.plan === "anual" ? "Anual" : d?.plan || "Sem plano"; // ex.: "Premium"
        setPlanLabel(plan);

        // Status mostrado
        let status = d?.subscriptionStatus || "sem assinatura";
        if ((status === "active" || status === "trialing") && d?.cancelAtPeriodEnd) {
          status = "cancelado (acesso até o fim do período)";
        }
        setStatusLabel(status);
      });

      // cleanup o snapshot quando trocar usuário
      return unsubDoc;
    });

    return () => unsubAuth && unsubAuth();
  }, []);

  // salvar edições
  const handleSaveEdit = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = doc(firestore, "usuarios", user.uid);
        if (newName) {
          await updateProfile(user, { displayName: newName });
          await updateDoc(userDoc, { nome: newName });
          setUserName(newName);
        }
        if (newPassword) await updatePassword(user, newPassword);
        alert("Dados atualizados com sucesso!");
      }
    } catch (error) {
      alert("Erro ao atualizar os dados.");
    }
    setOpenEdit(false);
  };

  // resetar coleções
  const handleResetAccount = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const collectionsToClear = ["metas", "lancamentos", "categorias"];
        for (const colName of collectionsToClear) {
          const qs = await getDocs(collection(firestore, `usuarios/${user.uid}/${colName}`));
          for (const d of qs.docs) {
            await deleteDoc(doc(firestore, `usuarios/${user.uid}/${colName}`, d.id));
          }
        }
        alert("Conta resetada com sucesso!");
      }
    } catch (error) {
      alert("Erro ao resetar a conta.");
    }
    setOpenReset(false);
  };

  // excluir conta
  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        await deleteUser(user);
        await signOut(auth);
        navigate("/authentication/sign-in");
      }
    } catch (error) {
      alert("Erro ao excluir a conta.");
    }
    setOpenDelete(false);
  };

  // assinar (Checkout)
  const handleSubscribe = async (priceId) => {
    try {
      const user = auth.currentUser;
      if (!user) return alert("Você precisa estar logado.");
      await createCheckoutSession({ uid: user.uid, priceId }); // redireciona dentro do serviço
    } catch (err) {
      alert("Erro ao iniciar assinatura: " + err.message);
    }
  };

  // cancelar (agenda no fim do período)
  const handleConfirmCancel = async () => {
    try {
      if (!perfil?.stripeSubscriptionId) {
        alert("Assinatura não localizada.");
        return;
      }
      setCancelLoading(true);
      await cancelAtPeriodEnd(perfil.stripeSubscriptionId);
      setOpenCancel(false);
      alert("Cancelamento agendado. Você manterá o acesso até o fim do período atual.");
      // onSnapshot refletirá quando o webhook atualizar o doc
    } catch (e) {
      alert("Não foi possível cancelar: " + e.message);
    } finally {
      setCancelLoading(false);
    }
  };

  // condições de exibição
  const isActive = perfil?.subscriptionStatus === "active" || perfil?.subscriptionStatus === "trialing";
  const canShowCancelBtn = isActive && !perfil?.cancelAtPeriodEnd && !!perfil?.stripeSubscriptionId;
  const canShowSubscribeBtns = !isActive && (perfil?.subscriptionStatus === "canceled" || !perfil?.subscriptionStatus);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        p={3}
        sx={{
          maxWidth: 600,
          margin: "0 auto",
          backgroundColor: darkMode ? "#121212" : "#FFFFFF",
          borderRadius: "12px",
          boxShadow: darkMode ? "0px 4px 10px rgba(255, 255, 255, 0.2)" : "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <MDTypography variant="h4" mb={2}>
          Minha Conta
        </MDTypography>

        <MDBox mb={2}>
          <MDTypography variant="body2">Nome Completo</MDTypography>
          <TextField value={userName} fullWidth variant="outlined" InputProps={{ readOnly: true }} />
        </MDBox>
        <MDBox mb={2}>
          <MDTypography variant="body2">E-mail</MDTypography>
          <TextField value={userEmail} fullWidth variant="outlined" InputProps={{ readOnly: true }} />
        </MDBox>

        {/* Assinatura */}
        <MDBox mt={4}>
          <MDTypography variant="h6">Assinatura</MDTypography>
          <MDBox mt={1} p={2} sx={{ backgroundColor: darkMode ? "#0e0e0e" : "#f6f7fb", borderRadius: "12px" }}>
            <MDTypography variant="body1">Plano Atual: {planLabel}</MDTypography>

            <MDTypography variant="body2" mt={0.5}>
              {isActive && !perfil?.cancelAtPeriodEnd && (
                <>Status: Ativo • renova em {perfil?.currentPeriodEnd?.toDate?.().toLocaleDateString?.() || "-"}</>
              )}
              {isActive && perfil?.cancelAtPeriodEnd && (
                <>Status: Cancelado • acesso até {perfil?.currentPeriodEnd?.toDate?.().toLocaleDateString?.() || "-"}</>
              )}
              {!isActive && (perfil?.subscriptionStatus === "canceled" || !perfil?.subscriptionStatus) && (
                <>Status: Sem assinatura</>
              )}
            </MDTypography>

            {/* Assinar (se não estiver ativo) */}
            {canShowSubscribeBtns && (
              <MDBox display="flex" gap={2} mt={2}>
                <MDButton color="info" onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_MENSAL)}>
                  Assinar Mensal
                </MDButton>
                <MDButton color="success" onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_ANUAL)}>
                  Assinar Anual
                </MDButton>
              </MDBox>
            )}

            {/* Cancelar (se ativo e sem cancelamento agendado) */}
            {canShowCancelBtn && (
              <MDBox mt={2}>
                <MDButton color="error" variant="outlined" onClick={() => setOpenCancel(true)}>
                  Cancelar plano
                </MDButton>
              </MDBox>
            )}
          </MDBox>
        </MDBox>

        {/* Outros botões */}
        <MDBox display="flex" justifyContent="space-between" mt={4}>
          <MDButton onClick={() => setOpenEdit(true)} color="info" variant="contained">
            Editar Conta
          </MDButton>
          <MDButton onClick={() => setOpenReset(true)} color="warning" variant="contained">
            Resetar Conta
          </MDButton>
        </MDBox>

        <MDBox display="flex" justifyContent="space-between" mt={4}>
          <MDButton onClick={() => setOpenDelete(true)} color="error" variant="outlined">
            Excluir Conta
          </MDButton>
          <MDButton onClick={() => setOpenSupport(true)} color="primary" variant="outlined">
            Contato com Suporte
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Modais */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Editar Conta</DialogTitle>
        <DialogContent>
          <TextField label="Novo Nome" fullWidth margin="normal" onChange={(e) => setNewName(e.target.value)} />
          <TextField
            label="Nova Senha"
            type="password"
            fullWidth
            margin="normal"
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleSaveEdit} color="info">
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReset}
        onClose={() => setOpenReset(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Resetar Conta</DialogTitle>
        <DialogContent>Tem certeza que deseja resetar sua conta? Todos os dados serão apagados.</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenReset(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleResetAccount} color="warning">
            Resetar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Excluir Conta</DialogTitle>
        <DialogContent>Tem certeza que deseja excluir sua conta permanentemente?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenDelete(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={handleDeleteAccount} color="error">
            Excluir
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Contato com Suporte</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">Para entrar em contato com o suporte, envie um e-mail para:</MDTypography>
          <MDTypography variant="h6" color="info">
            infofinnoplan@gmail.com
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenSupport(false)} color="primary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal de cancelamento */}
      <Dialog
        open={openCancel}
        onClose={() => setOpenCancel(false)}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[5],
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle>Cancelar plano</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            Tem certeza que deseja cancelar sua assinatura?
            <br />
            Você continuará com acesso até{" "}
            {perfil?.currentPeriodEnd?.toDate?.().toLocaleDateString?.() || "o fim do período atual"}. Não haverá
            renovação automática.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenCancel(false)} color="secondary">
            Voltar
          </MDButton>
          <MDButton onClick={handleConfirmCancel} color="error" disabled={cancelLoading}>
            {cancelLoading ? "Cancelando..." : "Confirmar cancelamento"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MinhaConta;
